import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {RoiReport, RoiReportKpi, RoiSetting} from '../../../../../models/measure/roi-report';
import {CommonConstant} from '../../../../app.constants';
import {ReportSectionEnum} from '../../../../../models/measure/report';
import {ReferenceDataService} from '../../../../services/reference-data/reference-data.service';
import {MeasureService} from '../../../../services/measure/measure.service';
import {UserService} from '../../../../services/user.service';
import {AchooPopupComponent} from '../../../../core/achoo-popup/achoo-popup.component';
import {ModalService} from '../../../../services/modal/modal.service';
import {PdfExportService} from '../../../../services/export-pdf/pdf-export.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'roi-report',
  templateUrl: './roi-report.component.html',
  styleUrls: ['./roi-report.component.scss']
})
export class RoiReportComponent implements OnInit, AfterViewInit  {
  static COMPONENT_NAME = 'RoiReportComponent';
  @ViewChild('roiSettingPopup') roiSettingPopup: AchooPopupComponent;
  @ViewChild('roiErrorNotification') errorNotification: AchooPopupComponent;

  @Input() roiReport: RoiReport;
  @Input() numberOfItemPerPage: number;
  // only use for PDF export. This indicate the index of current component
  @Input() pageIndex: number;
  @Output() changePage: EventEmitter<any> = new EventEmitter<any>();
  public roiSetting: RoiSetting = new RoiSetting();
  // curent page number of ROI report, start with 1
  public currentPage = 1;

  constructor(private userService: UserService,
              private measureService: MeasureService,
              private modalService: ModalService,
              private pdfService: PdfExportService,
              private translate: TranslateService) { }

  ngOnInit() {
    this.measureService.loadRoiSetting(this.userService.getCompanyId()).subscribe((res) => {
      if (res) {
        this.roiSetting = res;
      }
    });
  }

  ngAfterViewInit() {
    this.pdfService.imagedLoadedTrigger.subscribe(data => {
      if (data.component === `${RoiReportComponent.COMPONENT_NAME}${this.pageIndex}`
            && data.currentCounter === this.roiReport.datas.length) {
        this.pdfService.incrementNumberReportReady(`${RoiReportComponent.COMPONENT_NAME}${this.pageIndex}`);
      }
    });
  }

  openSetting() {
    this.modalService.openInCenter(this.roiSettingPopup, undefined, 'roi-settings-modal');
  }
  closeSetting() {
    this.modalService.close();
  }

  isCurrencyField(colName: string): boolean {
    return colName === CommonConstant.CURRENCY_COL;
  }

  get roiValue(): RoiReportKpi {
    if (!this.roiReport || !this.roiReport.kpis) {
      return null;
    }
    return this.roiReport.kpis.find(kpi => kpi.name.toLowerCase() === CommonConstant.ROI_COL.toLowerCase());
  }

  get postKpi(): RoiReportKpi {
    if (!this.roiReport || !this.roiReport.kpis) {
      return null;
    }
    return this.roiReport.kpis.find(kpi => kpi.name.toLowerCase() === CommonConstant.POSTS_COL.toLowerCase());
  }

  get influencersKpi(): RoiReportKpi {
    if (!this.roiReport || !this.roiReport.kpis) {
      return null;
    }
    return this.roiReport.kpis.find(kpi => kpi.name.toLowerCase() === CommonConstant.INFLUENCERS_COL.toLowerCase());
  }

  get impressionKpi(): RoiReportKpi {
    if (!this.roiReport || !this.roiReport.kpis) {
      return null;
    }
    return this.roiReport.kpis.find(kpi => kpi.name.toLowerCase() === CommonConstant.IMPRESSIONS_COL.toLowerCase());
  }

  get reachKpi(): RoiReportKpi {
    if (!this.roiReport || !this.roiReport.kpis) {
      return null;
    }
    return this.roiReport.kpis.find(kpi => kpi.name.toLowerCase() === CommonConstant.REACH_COL.toLowerCase());
  }

  get currency(): number {
    if (!this.roiReport) {
      return 0;
    }

    const [currency] = this.roiReport.kpis.filter((kpi) => this.isCurrencyField(kpi.name));
    return currency ? currency.value : 0;
  }

  /**
   * Currency value will not display in ROI KPI section
   * @returns {RoiReportKpi[]}
   */
  get roiKpiList(): RoiReportKpi[] {
    if (!this.roiReport) {
      return [];
    }
    return this.roiReport.kpis.filter((kpi) => !this.isCurrencyField(kpi.name));

  }

  get haveNoData() {
    if (!this.roiReport || !this.roiReport.datas) {
      return true;
    }

    return this.roiReport.datas.length === 0 && (!this.pageIndex || this.pageIndex === 0);
  }

  onChangePage(evt) {
    this.currentPage = evt;
    this.changePage.emit({
      reportSection: ReportSectionEnum.ROI_REPORT,
      criteriaChange: {page_no: evt}
    });
  }

  get maxPage() {
    if (!this.roiReport) {
      return 0;
    }
    const totalFullPage = Math.floor(this.roiReport.totalRow / this.numberOfItemPerPage);
    const hasPartialPage = (this.roiReport.totalRow % this.numberOfItemPerPage) > 0;
    return hasPartialPage ? totalFullPage + 1 : totalFullPage;
  }

  onSaveRoiSetting() {
    this.closeSetting();
    this.measureService.saveRoiSetting(this.roiSetting).subscribe((res) => {
      // reload ROI report
      this.changePage.emit({
        reportSection: ReportSectionEnum.ROI_REPORT,
        criteriaChange: {}
      });
    }, (error) => {
      this.modalService.open(this.errorNotification, null, true);
      this.openSetting();
    });
  }


  calculateIndex(index) {
    if (this.pageIndex) {
      if (this.pageIndex > 0) {
        return this.pageIndex * CommonConstant.MAX_ROI_ITEM_IN_PDF_PAGE + index - 1;
      } else {
        return this.pageIndex * CommonConstant.MAX_ROI_ITEM_IN_PDF_PAGE + index + 1;
      }
    }
    return (this.currentPage - 1) * this.numberOfItemPerPage + index + 1;
  }

  findCountryCode(influencer) {
    return influencer.country ? influencer.country.code : CommonConstant.INTERNATION_COUNTRY_CODE;
  }

  onLoadedAvatarImage() {
    this.pdfService.updateNumberImageLoaded(`${RoiReportComponent.COMPONENT_NAME}${this.pageIndex}`);
  }

  get activeAccount() {
    return this.userService.getCompanyId();
  }

  get isExportingPdfReport() {
    return this.pdfService.exportingPdf;
  }

  get listInfluencer() {
    if (!this.roiReport || !Array.isArray(this.roiReport.datas)) {
      return [];
    }
    const startIndex = (this.currentPage - 1) * this.numberOfItemPerPage;
    return this.roiReport.datas.slice(startIndex, startIndex + this.numberOfItemPerPage);
  }

  get shouldShowHeader() {
    return !this.pageIndex || this.pageIndex === 0;
  }

  get roiValueColHeader(): RoiReportKpi {
    if (!this.roiReport || !this.roiReport.kpis) {
      return null;
    }
    const currencyValue = this.roiReport.kpis.find(kpi => kpi.name.toLowerCase() === CommonConstant.CURRENCY_COL.toLowerCase());
    let currencySign = '';

    switch (currencyValue.value) {
      case 0:
        currencySign = CommonConstant.CURRENCY_SIGN.EURO;
        break;
      case 1:
        currencySign = CommonConstant.CURRENCY_SIGN.USD;
        break;
      case 2:
        currencySign = CommonConstant.CURRENCY_SIGN.DANISH_KRONE;
        break;
      case 3:
        currencySign = CommonConstant.CURRENCY_SIGN.NORWEGIAN_KRONER;
        break;
      case 4:
        currencySign = CommonConstant.CURRENCY_SIGN.SWEDISH_KRONA;
        break;
      case 5:
        currencySign = CommonConstant.CURRENCY_SIGN.POUND;
        break;
    }
    return this.translate.instant('measure.roi_table.header.roi', {currencySign: currencySign});
  }
}



// WEBPACK FOOTER //
// ./src/app/main/measure/content/roi-report/roi-report.component.ts